import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { VN_UTC_OFFSET_MS, vnDate } from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";

/**
 * Chọn kỳ báo cáo cho Phân tích và Lợi nhuận.
 *
 * Trạng thái nằm trong URL như mọi bộ lọc khác, nên một kỳ cụ thể chia sẻ và
 * đánh dấu trang được — kế toán gửi link "quý 3" cho giám đốc thay vì bảo nhau
 * bấm lại từng ô.
 *
 * Bỏ trống thì máy chủ dùng 12 tháng gần nhất, đúng mặc định của hệ thống cũ.
 * Vì vậy nút Đặt lại chỉ xoá tham số khỏi URL chứ không điền ngày mặc định vào
 * ô — điền vào sẽ khiến người dùng tưởng đó là lựa chọn của chính họ.
 */
export function PeriodFilter({
  from,
  to,
  onChange,
  onReset,
  period,
  preset,
  onPreset,
}: {
  from: string;
  to: string;
  onChange: (key: "from" | "to", value: string | null) => void;
  onReset: () => void;
  /** Kỳ máy chủ THỰC SỰ đã dùng, epoch ms — hiện ra khi người dùng chưa chọn gì. */
  period?: { from: number; to: number };
  /** Kỳ nhanh đang chọn (Phân tích bán hàng, yêu cầu v1.8 mục 1.2). Vắng `onPreset` thì không hiện nút kỳ nhanh. */
  preset?: PeriodPreset | null;
  onPreset?: (preset: PeriodPreset) => void;
}) {
  const { t } = useTranslation();
  const active = (!!from || !!to) && !preset;
  // from > to trả về bảng rỗng và trông như mất dữ liệu; nói thẳng ra.
  const reversed = !!from && !!to && from > to;
  const asDate = (ms: number) => new Date(ms).toLocaleDateString();

  return (
    <div className="flex flex-wrap items-end gap-3">
      {onPreset && (
        <div role="group" aria-label={t("insight.presetsLabel")} title={t("insight.presetHint")} className="flex items-center rounded-sm border border-line bg-surface p-0.5">
          {PERIOD_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={preset === p}
              onClick={() => onPreset(p)}
              className={
                "rounded-[3px] px-2.5 py-1.5 text-xs font-medium transition-colors " +
                (preset === p ? "bg-brand text-brand-on" : "text-ink-faint hover:text-ink-soft")
              }
            >
              {t(p === "day" ? "insight.presetDay" : p === "week" ? "insight.presetWeek" : "insight.presetMonth")}
            </button>
          ))}
        </div>
      )}
      <div className="w-44">
        <FieldLabel htmlFor="period-from">{t("insight.from")}</FieldLabel>
        <Input
          id="period-from"
          type="date"
          invalid={reversed}
          value={from}
          onChange={(e) => onChange("from", e.target.value || null)}
        />
      </div>
      <div className="w-44">
        <FieldLabel htmlFor="period-to">{t("insight.to")}</FieldLabel>
        <Input
          id="period-to"
          type="date"
          invalid={reversed}
          value={to}
          onChange={(e) => onChange("to", e.target.value || null)}
        />
      </div>
      {active && (
        <Button variant="ghost" size="sm" onClick={onReset}>
          <RotateCcw className="size-3.5" />
          {t("insight.reset")}
        </Button>
      )}
      {!active && !preset && period && (
        <span className="pb-2 text-xs text-ink-faint">
          {t("insight.defaultPeriod", { from: asDate(period.from), to: asDate(period.to) })}
        </span>
      )}
      {reversed && (
        <span className="pb-2 text-xs text-danger">{t("insight.rangeReversed")}</span>
      )}
    </div>
  );
}

/**
 * Ngày từ ô `<input type="date">` → epoch ms, quy về đầu/cuối ngày THEO GIỜ VIỆT NAM (CLAUDE.md: mọi ngày nghiệp vụ theo giờ Việt
 * Nam). Trước 11/10/2026 tính theo múi giờ máy người xem: người ở Trung Quốc (UTC+8) lọc "ngày 31" bị lệch một giờ so với ngày của
 * chứng từ, mà số chứng từ, "hôm nay" và mọi báo cáo phía máy chủ đều tính theo giờ Việt Nam.
 */
export function dayToMs(value: string, endOfDay: boolean): number | undefined {
  if (!value) return undefined;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  const start = Date.UTC(y, m - 1, d) - VN_UTC_OFFSET_MS;
  if (Number.isNaN(start)) return undefined;
  return endOfDay ? start + 86_400_000 - 1 : start;
}

/** Kỳ nhanh của Phân tích bán hàng (yêu cầu v1.8 mục 1.2). */
export const PERIOD_PRESETS = ["day", "week", "month"] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

/**
 * Kỳ TRƯỢT kết thúc hôm nay theo giờ Việt Nam, dạng ô ngày "YYYY-MM-DD" (yêu cầu v1.8 mục 1.2; kỳ trượt hay kỳ lịch là câu khách còn
 * hỏi — tự chốt kỳ trượt, khách bác lại được):
 *  - day   = hôm qua → hôm nay (PI lập sáng nay vẫn thấy, đơn của cả ngày hôm qua cũng thấy);
 *  - week  = 7 ngày tính cả hôm nay (05/10 → 11/10);
 *  - month = tháng trượt: từ ngày kế sau "cùng ngày tháng trước" → hôm nay (12/09 → 11/10; 31/03 → từ 01/03).
 * (Kiểm thử 11/10/2026: tuần từng ra 8 ngày, tháng 31–32 ngày vì tính cả hai đầu.)
 * Tính trên chuỗi ngày bằng UTC như CLAUDE.md cho phép (cộng trừ trên "YYYY-MM-DD").
 */
export function presetRange(preset: PeriodPreset, now: number = Date.now()): { from: string; to: string } {
  const to = vnDate(now);
  const [y, m, d] = to.split("-").map(Number) as [number, number, number];
  const at = (yy: number, mm: number, dd: number) => new Date(Date.UTC(yy, mm, dd)).toISOString().slice(0, 10);
  if (preset === "day") return { from: at(y, m - 1, d - 1), to };
  if (preset === "week") return { from: at(y, m - 1, d - 6), to };
  const lastDay = new Date(Date.UTC(y, m - 1, 0)).getUTCDate();
  return { from: at(y, m - 2, Math.min(d, lastDay) + 1), to };
}
